/**
 * API Service
 * Centralized API calls to the backend
 */

import config from "../config";

const API_URL = config.apiUrl;

/**
 * Helper function to handle API responses
 */
const handleResponse = async (response) => {
  const data = await response.json();
  if (!response.ok) {
    const error = data.message || data.errors || "An error occurred";
    throw new Error(typeof error === "object" ? JSON.stringify(error) : error);
  }
  return data;
};

/**
 * Authentication API
 */
export const authApi = {
  /**
   * Login user
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<{message: string, token: string, user: object}>}
   */
  login: async (email, password) => {
    const response = await fetch(`${API_URL}/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password }),
    });
    return handleResponse(response);
  },
};

/**
 * Blog API
 * Note: Admin uses /api prefix for all blog endpoints
 */
export const blogApi = {
  /**
   * Get all blogs
   * @returns {Promise<{blogs: Array}>}
   */
  getAll: async () => {
    const response = await fetch(`${API_URL}/api/blogs`);
    return handleResponse(response);
  },

  /**
   * Create a new blog
   * @param {object} blogData - Blog data
   * @param {string} blogData.title - Blog title
   * @param {string} blogData.content - Blog content (HTML from CKEditor)
   * @param {File[]} [blogData.pictures] - Optional array of image files
   * @returns {Promise<{message: string, blog: object}>}
   */
  create: async ({ title, content, pictures = [] }) => {
    const formData = new FormData();
    formData.append("title", title);
    formData.append("content", content);

    // Append each picture file
    pictures.forEach((file) => {
      formData.append("pictures", file);
    });

    const response = await fetch(`${API_URL}/api/add/blog`, {
      method: "POST",
      body: formData,
      // Note: Don't set Content-Type header for FormData
      // The browser will set it automatically with the boundary
    });
    return handleResponse(response);
  },
};

/**
 * Export all APIs
 */
const api = {
  auth: authApi,
  blog: blogApi,
};

export default api;
